// Central registry of every public placeholder page.
// Each entry has: path, eyebrow, title, description (intro), sections[].

export const publicPages = [
  {
    path: "/mission",
    eyebrow: "Our North Star",
    title: "Mission",
    description:
      "Aurora exists to make dating feel human again. Every person you meet here has been verified by an FBI Identity History Summary Check — because trust is the foundation every real connection deserves.",
    sections: [
      {
        heading: "Why we built Aurora",
        body:
          "Dating apps have quietly become the world's largest source of catfishing, romance fraud, and emotional harm. We refused to accept that as the cost of finding love. Aurora is our answer — a platform where fear is engineered out from day one.",
      },
      {
        heading: "What we believe",
        bullets: [
          "Where there is fear, there is no room for love.",
          "Verified identity is a human right in modern dating.",
          "Personality, values, and compatibility matter more than a swipe.",
          "Safety is not a premium feature. It is the product.",
        ],
      },
      {
        heading: "How we measure success",
        body:
          "Not by daily active users. Not by swipes. We measure success by the number of couples who tell us Aurora is the first place they felt truly safe being themselves — and by keeping our catfish reports at zero.",
      },
    ],
  },
  {
    path: "/labs",
    eyebrow: "Aurora Labs",
    title: "Labs",
    description:
      "Labs is where tomorrow's Aurora is born — safely, thoughtfully, and always in service of real love. We prototype in public and ship only what makes dating meaningfully safer or more meaningful.",
    sections: [
      {
        heading: "What's in the oven",
        bullets: [
          "AI ice-breakers that respect the 25-question personality graph.",
          "Photo Blur Swipe — meet minds before faces.",
          "Anonymous Mode — reveal your identity on your own terms.",
          "Mutual-consent Medical History exchange for matched partners.",
        ],
      },
      {
        heading: "Our research principles",
        body:
          "Every experiment must be safer than the alternative, respect user consent, and be reversible. If a feature can't clear those three bars, it doesn't leave Labs.",
      },
    ],
  },
  {
    path: "/success-stories",
    eyebrow: "Real people, real love",
    title: "Real Love Stories",
    description:
      "From first hello to forever — couples who found each other on Aurora share their journeys. When you take fear out of the equation, all that's left is love worth telling.",
    stories: [
      {
        title: "Mike & Jenny",
        location: "Seattle, WA",
        image: "/aurora/success-stories/mike-jenny.jpg",
        quote: "Love for coffee, rain, and each other. A truly serendipitous match.",
        byline: "Matched · Nov 2024",
      },
      {
        title: "Ravi & Priya",
        location: "Washington, DC",
        image: "/aurora/success-stories/ravi-priya.jpg",
        quote: "Shared ambitions and a deep connection. Aurora delivered.",
        byline: "Matched · Sep 2024",
      },
      {
        title: "Alex & Tyler",
        location: "Denver, CO",
        image: "/aurora/success-stories/alex-ben.jpg",
        quote: "A match for life and the outdoors. Aurora exceeded all expectations.",
        byline: "Matched · Mar 2025",
      },
      {
        title: "Evelyn & James",
        location: "Chicago, IL",
        image: "/aurora/success-stories/evelyn-james.jpg",
        quote: "Shared values and a love for the city. Beyond grateful for Aurora.",
        byline: "Matched · May 2024",
      },
      {
        title: "Nia & Caleb",
        location: "Los Angeles, CA",
        image: "/aurora/success-stories/nia-caleb.jpg",
        quote: "Finding my perfect match in my 20s was a dream. Aurora made it happen. Our life together is incredible.",
        byline: "Matched · Oct 2023",
      },
      {
        title: "Camila & Ryo",
        location: "Los Angeles, CA",
        image: "/aurora/success-stories/maria-kenji.jpg",
        quote: "Finding someone who truly understands my world in my 30s was a gift. Aurora's matching is brilliant. Everything I ever hoped for. Our life together is a dream.",
        byline: "Matched · Nov 2022",
      },
      {
        title: "Isabella & Mateo",
        location: "Miami, FL",
        image: "/aurora/success-stories/isabella-mateo.jpg",
        quote: "We were looking for someone who shares our love for the outdoors and beach lifestyle. Aurora's Florida-specific matching was incredible.",
        byline: "Matched · Aug 2026",
      },
      {
        title: "Yusuf & Fatma",
        location: "Dubai, AE",
        image: "/aurora/success-stories/yusuf-fatma.jpg",
        quote: "We love the depth of the profiles. The 25 questions helped us understand each other's core values before we even met.",
        byline: "Matched · Jun 2024",
      },
      {
        title: "Oliver & Sarah",
        location: "London, UK",
        image: "/aurora/success-stories/david-sarah.jpg",
        quote: "Love is even sweeter the second time around. Aurora's matching algorithm is spot on.",
        byline: "Matched · May 2024",
      },
      {
        title: "Javier & Sofia",
        location: "Barcelona, ES",
        image: "/aurora/success-stories/javier-sofia.jpg",
        quote: "We found our travel buddy! Our shared passion for exploring the world started with an Aurora match.",
        byline: "Matched · Jan 2024",
      },
      {
        title: "Samir & Jasmine",
        location: "Vancouver, BC",
        image: "/aurora/success-stories/david-aisha.jpg",
        quote: "After using other apps, Aurora's focus on real connection was refreshing. We found our best friend.",
        byline: "Matched · May 2023",
      },
      {
        title: "Ken & Yoko",
        location: "Kyoto, JP",
        image: "/aurora/success-stories/ken-aisha.jpg",
        quote: "Our shared love for hiking and travel started with a match. Aurora brought us together for our biggest adventure yet.",
        byline: "Matched · Aug 2024",
      },
      {
        title: "Maria & Alejandro",
        location: "Madrid, ES",
        image: "/aurora/success-stories/maria-alejandro.jpg",
        quote: "Finding someone who was just as ambitious and adventurous as I am felt impossible until I joined Aurora. We share the same drive.",
        byline: "Matched · Jan 2022",
      },
      {
        title: "Emiko & Marcus",
        location: "Florence, IT",
        image: "/aurora/success-stories/ayumi-kenji.jpg",
        quote: "Connecting across cultures was effortless with Aurora. Emiko is everything I ever hoped for. Our life together is a dream.",
        byline: "Matched · Oct 2025",
      },
      {
        title: "Fatima & Imran",
        location: "Rome, IT",
        image: "/aurora/success-stories/fatima-rohan.jpg",
        quote: "An intellectual and emotional match I once thought impossible. Aurora bridged worlds and made this Roman holiday a dream.",
        byline: "Matched · Nov 2023",
      },
      {
        title: "Hanna & Lars",
        location: "Tromso, NO",
        image: "/aurora/success-stories/hanna-lars.jpg",
        quote: "Aurora found us a profound connection under the Northern Lights. Our life together is a dream.",
        byline: "Matched · May 2021",
      },
      {
        title: "Adwoa & Kofi",
        location: "Accra, GH",
        image: "/aurora/success-stories/adwoa-kwame.jpg",
        quote: "Finding someone who truly understands the depth of my African heritage and modern life was key. Aurora nailed it.",
        byline: "Matched · Jun 2026",
      },
      {
        title: "Kavya & Arjun",
        location: "Agra, IN",
        image: "/aurora/success-stories/priya-arjun.jpg",
        quote: "We were looking for someone who shared our cultural values and love for heritage. Aurora made that connection effortless. We're so grateful.",
        byline: "Matched · May 2026",
      },
      {
        title: "Sakura & Erik",
        location: "Kyoto, JP",
        image: "/aurora/success-stories/hana-lars.jpg",
        quote: "Aurora's attention to detail in matching personality and interests is what drew us in. We couldn't be happier.",
        byline: "Matched · Feb 2026",
      },
      {
        title: "Akira & Yuna",
        location: "Tokyo, JP",
        image: "/aurora/success-stories/akira-mei.jpg",
        quote: "Aurora helped us find a profound connection that bridges our cultural backgrounds. We are thrilled.",
        byline: "Matched · Sep 2026",
      },
      {
        title: "Imani & Haruto",
        location: "Paris, FR",
        image: "/aurora/success-stories/mei-kenji.jpg",
        quote: "Aurora's incredible matching algorithms connected us across oceans. Our life together is a dream.",
        byline: "Matched · Feb 2021",
      },
      {
        title: "Ayumi & Kenji",
        location: "Tokyo, JP",
        image: "/aurora/success-stories/ayumi-kenji-tokyo.jpg",
        quote: "Connecting across cultures was seamless with Aurora. Ayumi is everything I ever hoped for. Our life together is a dream.",
        byline: "Matched · Apr 2022",
      },
      {
        title: "Sara & Nikhil",
        location: "Paris, FR",
        image: "/aurora/success-stories/sara-arjun.jpg",
        quote: "Aurora's incredible matching algorithms connected us across oceans. Our life together is a dream.",
        byline: "Matched · Jun 2023",
      },
      {
        title: "Kwame & Aisha",
        location: "Paris, FR",
        image: "/aurora/success-stories/kwame-aisha.jpg",
        quote: "Kwame is everything I hoped for in a match. Our life is a masterpiece.",
        byline: "Matched · Jul 2022",
      },
      {
        title: "Hiroshi & Mei",
        location: "Paris, FR",
        image: "/aurora/success-stories/hiroshi-mei.jpg",
        quote: "Hiroshi is my perfect match. Our shared love for music and art is incredible. Matching was a dream come true.",
        byline: "Matched · Jun 2024",
      },
      {
        title: "Rohan & Anjali",
        location: "Paris, FR",
        image: "/aurora/success-stories/rohan-anjali.jpg",
        quote: "Finding Rohan was effortless. He's the soulmate I always dreamed of. Our life together is an adventure.",
        byline: "Matched · Sep 2022",
      },
      {
        title: "Natasha & Ryan",
        location: "Venice, IT",
        image: "/aurora/success-stories/natasha-ryan.jpg",
        quote: "Venice is where our love story blossomed. Shared sunsets and endless gondola rides have deepened our connection.",
        byline: "Matched · Oct 2024",
      },
      {
        title: "Gloria & Richard",
        location: "Boston, MA",
        image: "/aurora/success-stories/gloria-david.jpg",
        quote: "Finding love again in our 50s was unexpected, but Aurora's matching was profound. Gloria is everything I ever hoped for. Our life together is a dream.",
        byline: "Matched · Jun 2022",
      },
      {
        title: "Amara & Andrés",
        location: "Philadelphia, PA",
        image: "/aurora/success-stories/amara-diego.jpg",
        quote: "Connecting across backgrounds was effortless with Aurora. Amara is everything I ever hoped for. Our life together is a dream.",
        byline: "Matched · Dec 2025",
      },
      {
        title: "Carlos & Valentina",
        location: "Miami, FL",
        image: "/aurora/success-stories/carlos-sofia.jpg",
        quote: "Brought us together across cultures. We are living our best life.",
        byline: "Matched · Jan 2025",
      },
      {
        title: "Leo & Chloe",
        location: "Austin, TX",
        image: "/aurora/success-stories/leo-chloe.jpg",
        quote: "Matching our artistic souls. Our life is now a beautiful collaboration.",
        byline: "Matched · Jul 2024",
      },
      {
        title: "Elena & Ben",
        location: "San Francisco, CA",
        image: "/aurora/success-stories/maria-ben.jpg",
        quote: "The algorithm perfectly matched our adventurous spirits and love for city life. Ben is my perfect match.",
        byline: "Matched · Oct 2025",
      },
      {
        title: "David & Anya",
        location: "Santa Barbara, CA",
        image: "/aurora/success-stories/david-anya.jpg",
        quote: "Shared values and a love for the coastal life brought us together. David is my dream partner.",
        byline: "Matched · Mar 2024",
      },
    ],
    sections: [
      {
        heading: "Share your story",
        body:
          "Found your person on Aurora? We'd love to hear from you. Write to us at support@theauroramatch.com with a photo and a short paragraph — with your permission, we may feature your journey here to inspire others.",
      },
    ],
  },
  {
    path: "/careers",
    eyebrow: "Join us",
    title: "Careers",
    description:
      "We're a small, obsessive team building the safest place on the internet to fall in love. If that mission moves you, we'd love to hear from you.",
    sections: [
      {
        heading: "How we work",
        bullets: [
          "Remote-first, deep-work culture.",
          "Small teams shipping user-visible change every week.",
          "Kindness is a job requirement, not a perk.",
          "Every role touches safety — because every role should.",
        ],
      },
      {
        heading: "Open roles",
        body:
          "New roles will be posted here as we grow. Send us your story any time at support@theauroramatch.com — we read every message.",
      },
    ],
  },
  {
    path: "/safety-center",
    eyebrow: "Your safety, our promise",
    title: "Safety Center",
    description:
      "Aurora combines FBI background verification, live moderation, and end-to-end encryption to keep you protected on every date — before, during, and after.",
    sections: [
      {
        heading: "How we keep Aurora safe",
        bullets: [
          "FBI Identity History Summary Check required before any user can access the app.",
          "Human admin review of every verification submission.",
          "Instant block and report tools on every profile.",
          "Bank-grade encryption for messages, photos, and personal data.",
          "24/7 moderation on any behaviour that violates our community rules.",
        ],
      },
      {
        heading: "Meeting in person",
        bullets: [
          "Always meet in a public place for the first few dates.",
          "Tell a friend where you're going and share your live location.",
          "Trust your gut — Aurora will support you if anything feels wrong.",
          "Report harassment or unsafe behaviour immediately.",
        ],
      },
      {
        heading: "If something happens",
        body:
          "Contact us at support@theauroramatch.com or use the in-app report button. We escalate every serious report within one hour and cooperate fully with law enforcement.",
      },
    ],
  },
  {
    path: "/newsroom",
    eyebrow: "Press & updates",
    title: "Newsroom",
    description:
      "Coverage, product announcements, and stories from the Aurora team. For press inquiries, reach us at support@theauroramatch.com — we'd love to talk.",
    press: [
      { name: "The Verge", tagline: "Tech" },
      { name: "TechCrunch", tagline: "Startups" },
      { name: "Wired", tagline: "Culture" },
      { name: "Forbes", tagline: "Business" },
    ],
    sections: [
      {
        heading: "Latest announcement",
        body:
          "Aurora launched in 2026 as the first FBI-verified dating platform. Watch this space for product updates, milestones, and press coverage as we grow.",
      },
      {
        heading: "Recent milestones",
        bullets: [
          "Feb 2026 — Photo Blur Swipe and Anonymous Mode ship to all users.",
          "Feb 2026 — 25-question compatibility engine launches.",
          "Jan 2026 — Aurora goes live with FBI Identity History Summary Check verification.",
        ],
      },
      {
        heading: "Media assets",
        body:
          "Need our logo, brand colors, or a founder quote? Email support@theauroramatch.com with 'Press' in the subject and we'll respond within one business day.",
      },
    ],
  },
  {
    path: "/safe-dating-tips",
    eyebrow: "How to date on Aurora",
    title: "Safe Dating Tips",
    description:
      "Verified profiles are only the beginning. Great dates take small, deliberate steps — here's what our safety team recommends before you say hello, meet up, or share more.",
    sections: [
      {
        heading: "Before you meet",
        bullets: [
          "Video call inside the app before your first in-person date.",
          "Check the 25-question compatibility score to see where you align.",
          "Never send money, gift cards, or crypto to a match — no exceptions.",
        ],
      },
      {
        heading: "Your first date",
        bullets: [
          "Choose a public, well-lit location you already know.",
          "Arrange your own transportation to and from the venue.",
          "Set a check-in time with a friend or family member.",
          "Keep the first meeting short — an hour is plenty.",
        ],
      },
      {
        heading: "Trust your instincts",
        body:
          "If a match pressures you to move off Aurora, asks for money, or refuses a video call — pause. Report them. Real people don't rush and real people don't ask for money.",
      },
    ],
  },
  {
    path: "/trust-safety",
    eyebrow: "Trust engineered in",
    title: "Trust & Safety",
    description:
      "We take a zero-tolerance stance on catfishing, harassment, and unverified users. Trust isn't a badge on Aurora — it's a system baked into every interaction.",
    sections: [
      {
        heading: "The five layers",
        bullets: [
          "Identity: FBI background verification for every user.",
          "Review: Human admin approval of every account.",
          "Behaviour: Automated detection of scam patterns.",
          "Reporting: One-tap block/report on every profile.",
          "Response: A safety team that reads every report.",
        ],
      },
      {
        heading: "Community standards",
        body:
          "Every user agrees to date honestly, respect boundaries, and never ask for money. Violations lead to permanent removal — no second chances for behaviour that puts anyone at risk.",
      },
      {
        heading: "Our promise to you",
        body:
          "If Aurora ever fails you, we want to hear about it. Email safety@theauroramatch.com and a real human will respond within 24 hours.",
      },
    ],
  },
  {
    path: "/faq",
    eyebrow: "Common questions",
    title: "FAQ",
    description:
      "How do FBI checks work? What happens after I match? Who sees my medical history? All answers, in one place — updated as Aurora grows.",
    sections: [
      {
        heading: "How does FBI verification work?",
        body:
          "You submit your FBI Identity History Summary Check reference number during onboarding. Our admin team confirms the record and approves your account within 24 hours.",
      },
      {
        heading: "What is Photo Blur Swipe?",
        body:
          "Photos in the discovery feed are lightly blurred by default so first impressions come from personality — bio, interests, and the 25-question compatibility score. Photos become clear once you and your match both like each other.",
      },
      {
        heading: "What is Anonymous Mode?",
        body:
          "Anonymous Mode hides your name and photos in discovery. Only after a mutual match does your identity reveal to that person. Toggle it on any time from your profile.",
      },
      {
        heading: "Is Aurora free?",
        body:
          "Signing up, verifying, and matching on Aurora is free. We may introduce premium features in the future, but core dating and safety will always be free.",
      },
      {
        heading: "How do I delete my account?",
        body:
          "Email support@theauroramatch.com from the address on your account and we'll delete your profile and data within 72 hours.",
      },
    ],
  },
  {
    path: "/how-we-connect",
    eyebrow: "The Aurora method",
    title: "How We Connect Daters",
    description:
      "25 questions. One FBI check. Admin-approved profiles. A compatibility engine that respects your time. This is how Aurora turns strangers into safe, meaningful matches.",
    sections: [
      {
        heading: "Step 1 — Verify",
        body:
          "Every user completes an FBI Identity History Summary Check and passes human admin review before ever swiping.",
      },
      {
        heading: "Step 2 — Tell us who you are",
        body:
          "You answer 25 carefully chosen questions covering values, lifestyle, family, faith, health, and relationship intent. No fluff — every question feeds compatibility.",
      },
      {
        heading: "Step 3 — Match on what matters",
        body:
          "Our compatibility engine weighs your answers against everyone verified nearby and surfaces the highest-alignment profiles first. You always see a percentage, so you know why we're suggesting each person.",
      },
      {
        heading: "Step 4 — Meet safely",
        body:
          "Chat inside Aurora with end-to-end encryption. Take the video call. Meet in a public place. And enjoy knowing the person across the table is exactly who they say they are.",
      },
    ],
  },
  {
    path: "/security",
    eyebrow: "Bank-grade protection",
    title: "Security",
    description:
      "256-bit encryption, hardware-backed key storage, and continuous vulnerability testing protect every message and photo you share on Aurora.",
    sections: [
      {
        heading: "Data in transit",
        body:
          "Every request between your device and Aurora is protected with TLS 1.3 and 256-bit AES. Nothing leaves your phone in plain text.",
      },
      {
        heading: "Data at rest",
        body:
          "Passwords are salted and hashed with bcrypt. Personal identifiers are stored in isolated, access-audited databases. Photos are served from a hardened object store with signed URLs.",
      },
      {
        heading: "Responsible disclosure",
        body:
          "Found a vulnerability? Email security@theauroramatch.com. We acknowledge every report within 24 hours and reward valid submissions.",
      },
    ],
  },
  {
    path: "/privacy",
    eyebrow: "Your data, your rules",
    title: "Privacy Policy",
    description:
      "You own your story. This policy explains exactly what Aurora collects, how we use it, who we never share it with, and the controls you have at every step.",
    sections: [
      {
        heading: "What we collect",
        bullets: [
          "Account info: name, email, date of birth.",
          "Verification: FBI reference number and status.",
          "Profile: your 25-question answers, bio, photos, preferences.",
          "Usage: swipes, matches, and messages (encrypted).",
        ],
      },
      {
        heading: "What we never do",
        bullets: [
          "We never sell your data.",
          "We never share your profile with advertisers.",
          "We never train external AI models on your private messages.",
        ],
      },
      {
        heading: "Your controls",
        body:
          "Delete your account, export your data, or update any field at any time. Requests are honoured within 72 hours. Email privacy@theauroramatch.com or use in-app settings.",
      },
    ],
  },
  {
    path: "/terms",
    eyebrow: "The house rules",
    title: "Terms of Use",
    description:
      "By using Aurora, you agree to date honestly, respect the community, and let us keep this place safe. These are the plain-language terms that make our platform work for everyone.",
    sections: [
      {
        heading: "You promise to",
        bullets: [
          "Be 18 or older and use your real, verified identity.",
          "Treat every user with respect — no harassment, no hate.",
          "Never ask another user for money, gifts, or financial help.",
          "Report anyone who violates these terms.",
        ],
      },
      {
        heading: "We promise to",
        bullets: [
          "Verify every user before they can match with you.",
          "Respond to safety reports within 24 hours.",
          "Never share your data with third parties for advertising.",
          "Give you the tools to leave and take your data with you at any time.",
        ],
      },
      {
        heading: "Termination",
        body:
          "We may suspend or terminate accounts that violate these terms. You may close your account at any time by writing to support@theauroramatch.com.",
      },
    ],
  },
  {
    path: "/cookie-policy",
    eyebrow: "Cookies at Aurora",
    title: "Cookie Policy",
    description:
      "Aurora uses cookies only where they make the product better — never to sell your data. Read exactly what we set, why we set it, and how you can turn them off.",
    sections: [
      {
        heading: "The three categories",
        bullets: [
          "Essential — required to keep you logged in and secure.",
          "Preference — remembers your theme, language, and region.",
          "Analytics — anonymised usage so we can improve the product.",
        ],
      },
      {
        heading: "Advertising cookies",
        body:
          "Aurora does not run third-party advertising and does not set any advertising cookies. Ever.",
      },
      {
        heading: "Turning them off",
        body:
          "You can disable non-essential cookies from your browser settings or from your Aurora account. Some parts of the app may not work as smoothly without essential cookies.",
      },
    ],
  },
  {
    path: "/privacy-choices",
    eyebrow: "Your privacy choices",
    title: "Your Privacy Choices",
    description:
      "Under U.S. state laws, you have the right to see, correct, and delete your data. Manage those choices here — no forms, no phone calls, no friction.",
    sections: [
      {
        heading: "Your rights",
        bullets: [
          "Right to know what personal information we hold.",
          "Right to correct inaccurate personal information.",
          "Right to delete your account and personal information.",
          "Right to opt out of any future data sales (Aurora does not sell data).",
        ],
      },
      {
        heading: "Exercise your rights",
        body:
          "Email privacy@theauroramatch.com from the address on your account with the request you'd like to make. We respond within 45 days as required by law — usually within 72 hours.",
      },
    ],
  },
  {
    path: "/legal",
    eyebrow: "Legal",
    title: "Legal",
    description:
      "Notices, licenses, and disclosures that keep Aurora accountable. If you need to serve legal process or contact our legal team, start here.",
    sections: [
      {
        heading: "Company",
        body:
          "Aurora is operated by Aurora Inc., a Delaware corporation. All legal notices should be sent to legal@theauroramatch.com.",
      },
      {
        heading: "Law enforcement requests",
        body:
          "We cooperate with valid legal process. Requests must be sent on official letterhead to legal@theauroramatch.com. We publish an annual transparency report of the requests we receive.",
      },
      {
        heading: "Open-source acknowledgements",
        body:
          "Aurora is built on the shoulders of open-source software. A full list of dependencies and licenses is available on request.",
      },
    ],
  },
  {
    path: "/site-map",
    eyebrow: "Everything Aurora",
    title: "Site Map",
    description:
      "Every page on Aurora, one link away. Use this as a launchpad to explore the entire platform — from Mission to Privacy Choices.",
    sections: [
      {
        heading: "Explore",
        bullets: [
          "Mission — /mission",
          "Labs — /labs",
          "Success Stories — /success-stories",
          "Careers — /careers",
          "Newsroom — /newsroom",
        ],
      },
      {
        heading: "Safety & help",
        bullets: [
          "Safety Center — /safety-center",
          "Safe Dating Tips — /safe-dating-tips",
          "Trust & Safety — /trust-safety",
          "FAQ — /faq",
          "How We Connect Daters — /how-we-connect",
        ],
      },
      {
        heading: "Legal",
        bullets: [
          "Security — /security",
          "Privacy Policy — /privacy",
          "Terms of Use — /terms",
          "Cookie Policy — /cookie-policy",
          "Your Privacy Choices — /privacy-choices",
          "Legal — /legal",
        ],
      },
    ],
  },
];
